import type { PayloadAction } from '@reduxjs/toolkit';
import type { UnitedCanvasReducerState } from './unitedCanvasReducer';
import { uniqueId } from '../../lib/uniqueId';
import type { FunnelType } from '../../types/enums/FunnelType';
import type {
  MetadataElementToAdd,
  MetadataElementConnection,
} from '../../components/pages/funnel-map-flow/metadata-elements/addMetadataElementsToFunnelMap';
import { addMetadataElementsToFunnelMap } from '../../components/pages/funnel-map-flow/metadata-elements/addMetadataElementsToFunnelMap';

export const metadataElementsReducer = {
  addMetadataElementsToCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      metadataElements: MetadataElementToAdd[];
      connections?: MetadataElementConnection[];
      sectionId?: string;
    }>,
  ) => {
    const { metadataElements, connections, sectionId } = action.payload;
    addMetadataElementsToFunnelMap(state.funnelMap, metadataElements, connections, sectionId);
  },
  moveMetadataElement: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      fullMetadataId: string;
      newPosition: FunnelMapPosition;
      sectionId?: string;
    }>,
  ) => {
    const { fullMetadataId, newPosition, sectionId } = action.payload;
    if (state.funnelMap.metadataElements[fullMetadataId]) {
      state.funnelMap.metadataElements[fullMetadataId].position = newPosition;
      state.funnelMap.metadataElements[fullMetadataId].sectionId = sectionId;
    }
  },
  setMetadataElementIcon: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      fullMetadataId: string;
      iconName: string | null;
    }>,
  ) => {
    const { fullMetadataId, iconName } = action.payload;
    state.funnelMap.metadataElements[fullMetadataId].icon = iconName;
  },

  setMetadataElementColor: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      fullMetadataId: string;
      color: string;
    }>,
  ) => {
    const { fullMetadataId, color } = action.payload;
    state.funnelMap.metadataElements[fullMetadataId].color = color;
  },
  removeMetadataElement: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      fullMetadataId: string;
    }>,
  ) => {
    const { fullMetadataId } = action.payload;
    delete state.funnelMap.metadataElements[fullMetadataId];
    delete state.funnelMap.metadataElementsData[fullMetadataId];
    Object.values(state.funnelMap.metadataElements).forEach((metadataElement) => {
      metadataElement.funnelLinks = metadataElement.funnelLinks.filter(
        (link) => link.funnelId !== fullMetadataId,
      );
    });
  },
  connectMetadataElementSteps: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      originFullMetadataId: string;
      target: {
        funnelId: string;
        funnelType: FunnelType;
        stageId?: string;
      };
    }>,
  ) => {
    const {
      originFullMetadataId: originMetadataId,
      target: { funnelId, stageId, funnelType },
    } = action.payload;
    const originFunnel = state.funnelMap.metadataElements[originMetadataId];
    if (!originFunnel) return;

    if (
      originFunnel.funnelLinks.some(
        (link) => link.funnelId === funnelId && link.stageId === stageId,
      )
    )
      return;

    originFunnel.funnelLinks.push({
      _id: uniqueId(),
      _type: funnelType,
      funnelId,
      stageId,
    });
  },

  removeMetadataElementFunnelConnection: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      originFullMetadataId: string;
      targetFunnelId: string;
      targetStageId?: string;
    }>,
  ) => {
    const { originFullMetadataId, targetFunnelId, targetStageId } = action.payload;
    const originFunnel = state.funnelMap.metadataElements[originFullMetadataId];
    if (!originFunnel) return;

    originFunnel.funnelLinks = originFunnel.funnelLinks.filter(
      (link) => !(link.funnelId === targetFunnelId && link.stageId === targetStageId),
    );
  },
};
